/**
 * AI Studio — Owner SaaS OS for prompts/models (FASE 14 P3).
 * Tenant never sees this surface.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import {
  FlaskConical, Library, GitCompare, RotateCcw, DollarSign,
  Split, Gauge, Play, Loader2, Sparkles, Shield,
} from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { apiFetch, clientRequestHeaders, ensureCsrfToken } from "@/lib/sm-api";

export const Route = createFileRoute("/admin/ops/ai/studio")({
  component: AiStudioPage,
  head: () => ({ meta: [{ title: "AI Studio — Owner" }] }),
});

type Tab =
  | "playground"
  | "library"
  | "versions"
  | "benchmark"
  | "costs"
  | "ab"
  | "health";

interface PromptRow {
  id: string;
  name: string;
  category: string;
  version: string;
  status: string;
  body: string;
  recommended_model?: string | null;
}

function AiStudioPage() {
  const [tab, setTab] = useState<Tab>("playground");
  const [prompts, setPrompts] = useState<PromptRow[]>([]);
  const [selected, setSelected] = useState<PromptRow | null>(null);
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userMessage, setUserMessage] = useState("Explicá stock crítico en 3 bullets.");
  const [preferMock, setPreferMock] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sandboxOut, setSandboxOut] = useState<Record<string, unknown> | null>(null);
  const [benchRows, setBenchRows] = useState<Array<Record<string, unknown>>>([]);
  const [diff, setDiff] = useState<Record<string, unknown> | null>(null);
  const [sims, setSims] = useState<Array<Record<string, unknown>>>([]);
  const [score, setScore] = useState<Record<string, unknown> | null>(null);
  const [health, setHealth] = useState<Record<string, unknown> | null>(null);
  const [abTraffic, setAbTraffic] = useState<10 | 25 | 50 | 100>(10);
  const [suggestions, setSuggestions] = useState<string[]>([]);

  async function loadLibrary() {
    const res = await fetch("/api/admin/ai/studio?view=library", { credentials: "include" });
    const data = await res.json() as { prompts?: PromptRow[]; error?: string };
    if (!res.ok) {
      toast.error(data.error ?? "No se pudo cargar la librería");
      return;
    }
    setPrompts(data.prompts ?? []);
    if (!selected && data.prompts?.[0]) {
      setSelected(data.prompts[0]);
      setSystemPrompt(data.prompts[0].body);
    }
  }

  async function loadOverview() {
    const res = await fetch("/api/admin/ai/studio?view=overview", { credentials: "include" });
    const data = await res.json() as { health?: Record<string, unknown> };
    setHealth(data.health ?? null);
  }

  useEffect(() => {
    void loadLibrary();
    void loadOverview();
  }, []);

  async function post(action: string, body: Record<string, unknown> = {}) {
    setBusy(true);
    try {
      const res = await apiFetch("/api/admin/ai/studio", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ action, ...body }),
      });
      const data = await res.json() as Record<string, unknown>;
      if (!res.ok) {
        toast.error(String(data.error ?? "Error"));
        return null;
      }
      if (data.message) toast.success(String(data.message));
      return data;
    } finally {
      setBusy(false);
    }
  }

  const tabs: { id: Tab; label: string; icon: typeof Play }[] = [
    { id: "playground", label: "Sandbox", icon: FlaskConical },
    { id: "library", label: "Prompt Library", icon: Library },
    { id: "versions", label: "Diff / Rollback", icon: GitCompare },
    { id: "benchmark", label: "Benchmark", icon: Gauge },
    { id: "costs", label: "Cost Simulator", icon: DollarSign },
    { id: "ab", label: "A/B + Score", icon: Split },
    { id: "health", label: "Health / Policies", icon: Shield },
  ];

  return (
    <div className="space-y-4 p-4 md:p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" /> AI Studio
          </h1>
          <p className="text-sm text-muted-foreground max-w-2xl">
            Sistema operativo del Owner para prompts, modelos y costos. No afecta producción
            (sandbox / benchmark). El tenant no ve esta pantalla.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={busy}
            onClick={async () => {
              await post("seed");
              await loadLibrary();
            }}
          >
            Seed library
          </Button>
          <Button size="sm" variant="outline" asChild>
            <a href="/admin/ops/ai/owner-center">Centro Owner</a>
          </Button>
        </div>
      </div>

      <nav className="flex flex-wrap gap-1 border-b pb-2">
        {tabs.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs",
              tab === t.id
                ? "bg-violet-100 text-violet-800 dark:bg-violet-950 dark:text-violet-200"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <t.icon className="h-3.5 w-3.5" />
            {t.label}
          </button>
        ))}
      </nav>

      {tab === "playground" && (
        <section className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-3">
            <label className="text-xs font-medium">System prompt</label>
            <textarea
              className="min-h-[220px] w-full rounded-md border bg-background p-3 text-sm font-mono"
              value={systemPrompt}
              onChange={(e) => setSystemPrompt(e.target.value)}
            />
            <label className="text-xs font-medium">User message</label>
            <textarea
              className="min-h-[80px] w-full rounded-md border bg-background p-3 text-sm"
              value={userMessage}
              onChange={(e) => setUserMessage(e.target.value)}
            />
            <label className="flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={preferMock}
                onChange={(e) => setPreferMock(e.target.checked)}
              />
              Prefer mock (sin tocar providers productivos)
            </label>
            <Button
              disabled={busy || !systemPrompt.trim()}
              onClick={async () => {
                const data = await post("sandbox", {
                  systemPrompt,
                  userMessage,
                  preferMock,
                });
                if (data?.result) setSandboxOut(data.result as Record<string, unknown>);
              }}
            >
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              Ejecutar sandbox
            </Button>
          </div>
          <div className="rounded-xl border p-4 space-y-2">
            <h2 className="text-sm font-semibold">Preview</h2>
            {!sandboxOut && (
              <p className="text-xs text-muted-foreground">Corré el sandbox para ver resultado, costo y tiempo.</p>
            )}
            {sandboxOut && (
              <pre className="max-h-[420px] overflow-auto rounded-md bg-muted/40 p-3 text-xs whitespace-pre-wrap">
                {JSON.stringify(sandboxOut, null, 2)}
              </pre>
            )}
          </div>
        </section>
      )}

      {tab === "library" && (
        <section className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Registry de prompts por categoría. Nunca hardcodear en runtime — seed + DB.
          </p>
          <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            {prompts.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  setSelected(p);
                  setSystemPrompt(p.body);
                  setTab("playground");
                }}
                className="rounded-xl border p-3 text-left hover:border-violet-400"
              >
                <div className="text-sm font-medium">{p.name}</div>
                <div className="text-[11px] text-muted-foreground">
                  {p.category} · {p.version} · {p.status}
                </div>
              </button>
            ))}
            {prompts.length === 0 && (
              <p className="text-sm text-muted-foreground">Sin prompts — usá Seed library.</p>
            )}
          </div>
        </section>
      )}

      {tab === "versions" && (
        <section className="space-y-3 max-w-2xl">
          <p className="text-xs text-muted-foreground">
            Prompt Diff estilo Git + rollback a cualquier versión. Cambios críticos → review.
          </p>
          <select
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={selected?.id ?? ""}
            onChange={(e) => {
              const p = prompts.find((x) => x.id === e.target.value) ?? null;
              setSelected(p);
              if (p) setSystemPrompt(p.body);
            }}
          >
            {prompts.map((p) => (
              <option key={p.id} value={p.id}>{p.name} ({p.version})</option>
            ))}
          </select>
          <textarea
            className="min-h-[160px] w-full rounded-md border bg-background p-3 text-sm font-mono"
            value={systemPrompt}
            onChange={(e) => setSystemPrompt(e.target.value)}
          />
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              disabled={busy || !selected}
              onClick={async () => {
                if (!selected) return;
                const data = await post("version", {
                  promptId: selected.id,
                  body: systemPrompt,
                  comment: "Edición desde AI Studio",
                  costDeltaPct: -18,
                  latencyDeltaPct: -12,
                });
                if (data?.diff) setDiff(data.diff as Record<string, unknown>);
                await loadLibrary();
              }}
            >
              Guardar versión
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !selected}
              onClick={async () => {
                if (!selected) return;
                const data = await post("diff", {
                  promptId: selected.id,
                  costDeltaPct: -18,
                  latencyDeltaPct: -12,
                });
                if (data?.diff) setDiff(data.diff as Record<string, unknown>);
              }}
            >
              <GitCompare className="h-4 w-4" /> Ver diff
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={busy || !selected}
              onClick={async () => {
                if (!selected) return;
                await post("rollback", { promptId: selected.id });
                await loadLibrary();
              }}
            >
              <RotateCcw className="h-4 w-4" /> Rollback
            </Button>
            <Button
              size="sm"
              variant="secondary"
              disabled={busy || !selected}
              onClick={async () => {
                if (!selected) return;
                await post("approve_prompt", { promptId: selected.id });
                await loadLibrary();
              }}
            >
              Aprobar productivo
            </Button>
          </div>
          {diff && (
            <pre className="rounded-md bg-muted/40 p-3 text-xs whitespace-pre-wrap">
              {JSON.stringify(diff, null, 2)}
            </pre>
          )}
        </section>
      )}

      {tab === "benchmark" && (
        <section className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Mismo prompt en Claude / GPT / Gemini / Groq (+ mock). Calidad, tiempo, costo, grounding, citas, longitud.
          </p>
          <Button
            disabled={busy}
            onClick={async () => {
              const data = await post("benchmark", {
                systemPrompt: systemPrompt || selected?.body,
                userMessage,
                providers: ["mock", "anthropic", "openai", "gemini", "groq"],
              });
              if (data?.rows) setBenchRows(data.rows as Array<Record<string, unknown>>);
              const sug = data?.suggestions;
              if (Array.isArray(sug)) {
                setSuggestions(
                  sug.map((s) =>
                    typeof s === "string" ? s : String((s as { message?: string }).message ?? JSON.stringify(s)),
                  ),
                );
              }
            }}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Gauge className="h-4 w-4" />}
            Correr benchmark
          </Button>
          <div className="overflow-x-auto rounded-xl border">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/30 text-left">
                  <th className="p-2">Provider</th>
                  <th className="p-2">Modelo</th>
                  <th className="p-2">ms</th>
                  <th className="p-2">Costo</th>
                  <th className="p-2">Len</th>
                  <th className="p-2">Ground</th>
                  <th className="p-2">Citas</th>
                  <th className="p-2">Calidad</th>
                </tr>
              </thead>
              <tbody>
                {benchRows.map((r, i) => (
                  <tr key={i} className="border-b">
                    <td className="p-2">{String(r.provider ?? "—")}</td>
                    <td className="p-2">{String(r.model ?? "—")}</td>
                    <td className="p-2">{String(r.duration_ms ?? "—")}</td>
                    <td className="p-2">{String(r.cost_microcents ?? "—")}</td>
                    <td className="p-2">{String(r.length ?? r.output_length ?? "—")}</td>
                    <td className="p-2">{String(r.grounding ?? "—")}</td>
                    <td className="p-2">{String(r.citations ?? "—")}</td>
                    <td className="p-2">{String(r.quality ?? "—")}</td>
                  </tr>
                ))}
                {benchRows.length === 0 && (
                  <tr><td className="p-3 text-muted-foreground" colSpan={8}>Sin corridas aún.</td></tr>
                )}
              </tbody>
            </table>
          </div>
          {suggestions.length > 0 && (
            <ul className="list-disc pl-5 text-xs text-muted-foreground space-y-1">
              {suggestions.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          )}
        </section>
      )}

      {tab === "costs" && (
        <section className="space-y-3 max-w-xl">
          <p className="text-xs text-muted-foreground">
            Cost Simulator antes de cambiar un prompt productivo.
          </p>
          <Button
            disabled={busy}
            onClick={async () => {
              const data = await post("cost_sim", {
                modelIds: ["claude-sonnet-4-5", "gpt-4o", "gemini-2.0-flash", "mock-model"],
              });
              if (data?.simulations) setSims(data.simulations as Array<Record<string, unknown>>);
            }}
          >
            <DollarSign className="h-4 w-4" /> Simular
          </Button>
          <div className="space-y-2">
            {sims.map((s, i) => (
              <div key={i} className="rounded-lg border p-3 text-sm">
                <div className="font-medium">{String(s.model_id ?? s.model ?? "modelo")}</div>
                <div className="text-xs text-muted-foreground">
                  ~${String(s.monthly_usd ?? s.estimated_monthly_usd ?? "—")}/mes · diario $
                  {String(s.daily_usd ?? s.estimated_daily_usd ?? "—")}
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {tab === "ab" && (
        <section className="space-y-3 max-w-xl">
          <p className="text-xs text-muted-foreground">
            A/B: 10% → 25% → 50% → 100%. Prompt Score: calidad, costo, grounding, aprobaciones, satisfacción, tiempo, retry.
          </p>
          <div className="flex items-center gap-2 text-sm">
            Tráfico: <strong>{abTraffic}%</strong>
            <Button
              size="sm"
              variant="outline"
              onClick={async () => {
                const data = await post("ab_step", { trafficPct: abTraffic });
                if (data?.traffic_pct) setAbTraffic(Number(data.traffic_pct) as 10 | 25 | 50 | 100);
              }}
            >
              Subir tráfico
            </Button>
            <Button
              size="sm"
              disabled={busy || !selected}
              onClick={async () => {
                if (!selected) return;
                await post("ab_create", {
                  promptId: selected.id,
                  controlVersion: selected.version,
                  treatmentVersion: "v_next",
                  trafficPct: abTraffic,
                });
              }}
            >
              Crear A/B
            </Button>
          </div>
          <Button
            size="sm"
            variant="secondary"
            disabled={busy || !selected}
            onClick={async () => {
              const data = await post("score", {
                promptId: selected?.id,
                version: selected?.version,
                quality: 0.78,
                costEfficiency: 0.7,
                grounding: 0.72,
                approvalRate: 0.85,
                satisfaction: 0.74,
                latencyScore: 0.68,
                retryRate: 0.06,
              });
              if (data?.score) setScore(data.score as Record<string, unknown>);
            }}
          >
            Calcular Prompt Score
          </Button>
          {score && (
            <pre className="rounded-md bg-muted/40 p-3 text-xs">{JSON.stringify(score, null, 2)}</pre>
          )}
        </section>
      )}

      {tab === "health" && (
        <section className="space-y-3">
          <p className="text-xs text-muted-foreground">
            Health, rate limits y policies (vista sobre salud de proveedores y reglas).
          </p>
          <Button size="sm" variant="outline" onClick={() => void loadOverview()}>Refresh</Button>
          <pre className="rounded-md bg-muted/40 p-3 text-xs overflow-auto max-h-[480px]">
            {JSON.stringify(health ?? { note: "Sin snapshot" }, null, 2)}
          </pre>
          <p className="text-[11px] text-muted-foreground">
            Observabilidad de llamadas productivas sigue en el servicio de IA y la auditoría. Studio agrega corridas sandbox/benchmark.
          </p>
        </section>
      )}
    </div>
  );
}
